import React, { useEffect, useState } from 'react'

import { logErrorAsync } from '_lib/utility.js'

import Header from '_components/header/index.jsx'
import SettingsTab from './settings-tab/index.jsx'

import AccountSettings from './AccountSettings.jsx'
import ChangePassword from './ChangePassword.jsx'
import Invitations from './Invitations.jsx'
import ManageCollaborators from './ManageCollaborators.jsx'
import UserInternalSettings from './UserInternalSettings.jsx'
import { PermissionSets } from './permission-sets/index.jsx'

import { getOrgs, getAllUsers } from '_services/permissions'
import { NEW_USER_INITIAL, OPTION_NAMES } from './constants.js'
import {
  getTeamInfo,
  formatOrg,
  sendInvite,
  updatePermissions,
  updateOrg,
  delUser,
  checkAdmin,
  getOptions,
} from './helper.js'

import './styles.scss'

const SettingsPage = () => {
  const [usersList, setUsersList] = useState(null)
  const [permissions, setPermissions] = useState(null)
  const [invitations, setInvitations] = useState([])
  const [orgOptions, setOrgOptions] = useState([])

  const [selectedOption, setSelectedOption] = useState(OPTION_NAMES.ACCOUNT_SETTINGS)
  const [editingUser, setEditingUser] = useState(null)
  const [newUsers, setNewUsers] = useState(NEW_USER_INITIAL)

  const isShareThis = window._isShareThisUser
  const isAdmin = checkAdmin()
  const OPTIONS = getOptions({ isShareThis, isAdmin, isEditing: editingUser })

  const onSelectOption = (option) => {
    setSelectedOption(option)
  }

  useEffect(() => {
    try {
      getTeamInfo({ setPermissions, setInvitations })
      getOrgs().then(({ orgs }) => setOrgOptions(formatOrg(orgs)))
      getAllUsers().then(({ users }) => setUsersList(users))
    } catch (error) {
      logErrorAsync(error)
    }
  }, [])

  const handleAddUsers = () => {
    newUsers.emails.forEach((email) =>
      sendInvite({ email, roles: newUsers.permissions, org: newUsers.org })
    )
  }

  const handleSaveUser = () => {
    const { _id, org, permissions } = editingUser
    updateOrg({ id: _id, org: org })
    updatePermissions({ id: _id, roles: { ...permissions } })
  }

  const handleEditUser = (id) => () => {
    const userData = usersList.find((user) => user._id === id)
    setEditingUser({
      permissions: permissions[id],
      ...userData,
    })
    setSelectedOption(OPTION_NAMES.EDIT_COLLABORATOR)
  }

  const handleDeleteUser = (id, email) => {
    delUser({ id, email })
  }

  const handleReinvite = (user) => {
    sendInvite({ email: user.email, roles: user.permissions ?? user.roles, org: user.org })
  }

  const getActions = () => {
    switch (selectedOption) {
      case OPTION_NAMES.INVITE_NEW_MEMBER:
        return [
          {
            children: 'Add Users',
            onClick: handleAddUsers,
          },
        ]
      case OPTION_NAMES.EDIT_COLLABORATOR:
        return [
          {
            children: 'Save User',
            onClick: handleSaveUser,
          },
        ]
    }
  }

  return (
    <div className="settings">
      <Header title="Settings" actions={getActions()} />
      <div>
        <SettingsTab options={OPTIONS} onSelect={onSelectOption} active={selectedOption} />
        <Choose>
          <When condition={selectedOption === OPTION_NAMES.ACCOUNT_SETTINGS}>
            <div className="settings__account">
              <AccountSettings />
              <hr />
              <ChangePassword />
            </div>
          </When>
          <When condition={selectedOption === OPTION_NAMES.MANAGE_COLLABORATORS}>
            <ManageCollaborators
              users={usersList}
              orgOptions={orgOptions}
              handleEditUser={handleEditUser}
              handleDeleteUser={handleDeleteUser}
            />
          </When>
          <When condition={selectedOption === OPTION_NAMES.EDIT_COLLABORATOR}>
            <UserInternalSettings
              key="edit"
              type="edit"
              editingUser={editingUser}
              setEditingUser={setEditingUser}
              orgOptions={orgOptions}
            />
          </When>
          <When condition={selectedOption === OPTION_NAMES.INVITE_NEW_MEMBER}>
            <UserInternalSettings
              key="new"
              type="new"
              newUsers={newUsers}
              setNewUsers={setNewUsers}
              orgOptions={orgOptions}
            />
          </When>
          <When condition={selectedOption === OPTION_NAMES.INVITATIONS}>
            <Invitations
              invitations={invitations}
              orgOptions={orgOptions}
              handleReinvite={handleReinvite}
            />
          </When>
          <When condition={selectedOption === OPTION_NAMES.MANAGE_PERMISSION_SETS}>
            <PermissionSets />
          </When>
        </Choose>
      </div>
    </div>
  )
}

// export
export default SettingsPage
